// eVizitor pre-check: flags missing DOB / documents / expired passports per arrival before the CSV is downloaded.
import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Download, Loader2, ShieldCheck, X } from 'lucide-react';
import { apiCall } from './_shared';

export const fetchEvizitorPrecheck = (dateStr) => apiCall(`/api/admin/reception/export/evizitor/precheck?date_str=${dateStr || ''}`);

const Problem = ({ code, labels }) => (
  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 text-red-800 text-[11px] font-semibold" data-testid={`evizitor-problem-${code}`}>
    <AlertTriangle className="w-3 h-3" /> {labels[code] || code}
  </span>
);

const ProblemList = ({ codes, labels }) => {
  const items = codes.map((p) => <Problem key={p} code={p} labels={labels} />);
  return <>{items}</>;
};

const BookingRow = ({ b, labels, onOpen }) => {
  const guests = (b.guests || []).map((g, i) => {
    const GIcon = g.problems.length ? AlertTriangle : CheckCircle2;
    const gIconCls = g.problems.length ? 'w-3.5 h-3.5 text-red-500' : 'w-3.5 h-3.5 text-emerald-500';
    const role = g.is_primary ? 'primary' : (g.is_child ? 'child' : 'companion');
    return (
    <div key={i} className="flex flex-wrap items-center gap-1 text-xs pl-4" data-testid={`evizitor-guest-${b.booking_id}-${i}`}>
      <GIcon className={gIconCls} />
      <span className="font-medium text-slate-800">{g.name}</span>
      <span className="text-slate-400">{role}</span>
      <ProblemList codes={g.problems} labels={labels} />
    </div>
    );
  });
  const cardCls = b.ok ? 'border-emerald-200 bg-emerald-50/40' : 'border-red-200 bg-red-50/40';
  const Icon = b.ok ? CheckCircle2 : AlertTriangle;
  const iconCls = b.ok ? 'w-4 h-4 text-emerald-600' : 'w-4 h-4 text-red-600';
  return (
    <div className={`border rounded-lg p-2 space-y-1 ${cardCls}`} data-testid={`evizitor-booking-${b.booking_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Icon className={iconCls} />
        <span className="font-semibold text-sm text-slate-900">{b.guest_name || 'Guest'}</span>
        <span className="text-xs text-slate-500">{b.room_name || ''}</span>
        <ProblemList codes={b.problems} labels={labels} />
        {!b.ok && <button type="button" onClick={() => onOpen(b)} className="ml-auto text-xs underline text-orange-700 hover:text-orange-900" data-testid={`evizitor-fix-${b.booking_id}`}>Open folio to fix</button>}
      </div>
      {guests}
    </div>
  );
};

const EvizitorPrecheckModal = ({ dateStr, onClose, onDownload, onOpenFolio }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    fetchEvizitorPrecheck(dateStr).then((d) => alive && setData(d)).catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [dateStr]);
  const rows = (data?.bookings || []).map((b) => <BookingRow key={b.booking_id} b={b} labels={data.labels} onOpen={(x) => { onOpenFolio?.(x); }} />);
  const headerCls = !data ? 'bg-slate-700' : data.ok ? 'bg-emerald-600' : 'bg-red-600';
  let title = 'Checking guest data…';
  if (data && data.ok) title = `All clear · ${data.total_guests} guests on ${data.total_bookings} bookings`;
  else if (data) title = `${data.issues} issue(s) on ${data.bookings_with_issues} of ${data.total_bookings} bookings`;
  const allOk = Boolean(data && data.ok);
  const downloadCls = allOk ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-orange-600 hover:bg-orange-700';
  const downloadLabel = allOk ? 'Download CSV' : 'Download anyway';
  const showWarning = Boolean(data && !data.ok);
  const showLoading = !data && !err;
  const showEmpty = Boolean(data && !rows.length);
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="evizitor-precheck-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className={`p-4 rounded-t-xl text-white flex items-start justify-between ${headerCls}`}>
          <div>
            <p className="text-[10px] uppercase tracking-widest opacity-90 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> eVizitor pre-check</p>
            <h3 className="text-lg font-bold" data-testid="evizitor-precheck-title">{title}</h3>
            <p className="text-xs opacity-90">Arrivals {dateStr} · eVizitor rejects rows with missing birth dates, document numbers or nationality.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-white/20" aria-label="Close" data-testid="evizitor-precheck-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto space-y-2 flex-1">
          {err && <div className="text-sm text-red-700">{err}</div>}
          {showLoading && <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
          {showEmpty && <div className="text-sm text-slate-500 text-center py-6">No arrivals with folios on this date.</div>}
          {rows}
        </div>
        <div className="p-3 border-t flex flex-wrap gap-2 justify-end items-center">
          {showWarning && <span className="text-xs text-red-700 mr-auto">Fix the flagged guests first — or download anyway and correct in eVizitor.</span>}
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded hover:bg-slate-100">Cancel</button>
          <button type="button" onClick={onDownload} disabled={!data} className={`px-3 py-1.5 text-sm rounded font-semibold text-white inline-flex items-center gap-1 disabled:opacity-50 ${downloadCls}`} data-testid="evizitor-precheck-download">
            <Download className="w-4 h-4" /> {downloadLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EvizitorPrecheckModal;
